<template>
  <DefaultLayout>
    <div slot="content">
      <!-- FEATURE LIMITED DIALOG-->
      <v-dialog v-model="featureLimitedModal" width="400" persistent>
        <v-card>
          <v-card-title>Feature limited</v-card-title>
          <v-card-text>{{ featureLimitedText || featureLimitedDefaultText }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn tile text color="blue" @click="featureLimitedModal = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- END FEATURE LIMITED DIALOG-->
      <v-tabs slider-color="black" active-class="v-tabs_active_item" v-model="settingsTabs">
        <v-tab class="font-weight-bold" :ripple="false">General setting</v-tab>
        <v-tab v-if="$store.getters.isSystemAdmin" class="font-weight-bold" :ripple="false"
          >SMS templates</v-tab
        >
        <v-tab class="font-weight-bold" :ripple="false">Branding</v-tab>
      </v-tabs>
      <v-divider></v-divider>
      <v-tabs-items v-model="settingsTabs">
        <v-tab-item>
          <v-form ref="settingsForm" class="py-6">
            <v-switch
              class="mt-0"
              label="Do not send request cancelled notification email"
              v-model="organization.suppressCancelRequestEmail"
            ></v-switch>
            <v-btn small tile dark depressed color="blue" :loading="loading" @click="saveSettings"
              >Save Settings</v-btn
            >
          </v-form>
        </v-tab-item>
        <v-tab-item v-if="$store.getters.isSystemAdmin">
          <v-data-table
            :headers="smsTableHeaders"
            :loading="smsLoading"
            :items="smsTemplates.list"
            hide-default-footer
          >
            <template v-slot:item.edit="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" text icon color="green" @click="editSmsTemplate(item)"
                    ><v-icon>edit</v-icon></v-btn
                  >
                </template>
                <span>Edit SMS Template</span>
              </v-tooltip>
            </template>
          </v-data-table>
          <div v-if="computedSmsTemplateTypes.length" class="text-right">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  fab
                  dark
                  small
                  depressed
                  color="green"
                  @click="addAccountSmsTemplate"
                  ><v-icon>add</v-icon></v-btn
                >
              </template>
              <span>Add SMS Template</span>
            </v-tooltip>
          </div>
        </v-tab-item>
        <!-- BRANDING -->
        <v-tab-item>
          <v-card
            max-width="960"
            elevation="0"
            class="mt-4"
            v-if="$store.getters.isSummaryRequestCreator"
          >
            <v-card-title>Add Logo</v-card-title>
            <v-card-text>
              <p class="font-weight-light body-2">
                Utilize this logo to personalize guest requests.
              </p>
              <h3 class="title black--text mb-2">Logo requirements</h3>
              <ul class="requirements-list">
                <li
                  v-for="item in logoRequirements"
                  :key="item.label"
                  class="font-weight-light body-2"
                >
                  <b class="black--text">{{ item.label }}</b> {{ item.description }}
                </li>
              </ul>

              <v-form ref="newItemForm">
                <v-file-input
                  v-model="logo"
                  placeholder="Select Logo"
                  label="Logo file"
                  multiple
                  show-size
                  prepend-icon="fas fa-file-upload"
                >
                  <template v-slot:selection="{ text }">
                    <v-chip small label color="primary">
                      {{ text }}
                    </v-chip>
                  </template>
                </v-file-input>
              </v-form>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                tile
                depressed
                :disabled="!logo.length"
                color="blue"
                @click="addLogo"
                :loading="createInProgress"
                ><span class="button-color">Add Logo</span></v-btn
              >
              <v-btn
                outlined
                color="blue"
                :disabled="!organization.logo"
                @click="removeLogo"
                :loading="removeInProgress"
                >Remove Logo</v-btn
              >
            </v-card-actions>
            <v-card-text v-if="organization.logo">
              <h3 class="title black--text mb-2">Your currently downloaded Logo</h3>
              <v-img max-width="300" contain :src="organization.logo"></v-img>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <!--END BRANDING -->
      </v-tabs-items>
      <v-dialog v-model="smsDialog" width="400">
        <v-card>
          <v-card-title>{{
            smsTemplateEdit ? 'Edit SMS Template' : 'Add SMS Template'
          }}</v-card-title>
          <v-card-text>
            <v-form ref="smsTemplateForm">
              <v-text-field
                required
                v-model="currentSmsTemplate.sender"
                label="Sender"
                type="text"
                :rules="smsSenderRules"
              ></v-text-field>
              <v-textarea
                rows="3"
                required
                v-model="currentSmsTemplate.body"
                label="Body"
                type="text"
                :rules="[(v) => !!v || 'Body text is required']"
              ></v-textarea>
              <v-select
                v-if="!smsTemplateEdit"
                item-text="typeString"
                item-value="type"
                v-model="currentSmsTemplate.type"
                :items="computedSmsTemplateTypes"
                :rules="[(v) => !!v || 'Type is required']"
                label="Type"
              >
              </v-select>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn tile text color="blue" @click="smsDialog = false">Close</v-btn>
            <v-btn dark tile depressed color="blue" @click="saveSmsTemplate">{{
              smsTemplateEdit ? 'Save' : 'Add'
            }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </DefaultLayout>
</template>

<script>
import uuidv4 from 'uuid/v4';
import { mapState } from 'vuex';
import {
  CHECK_ORGANIZATIONS,
  GET_ACCOUNT_ESIGNTEMPLATES,
  SAVE_ACCOUNT_CUSTOM_SETTINGS,
  GET_ACCOUNT_SMS_TEMPLATES,
  UPDATE_CUSTOM_SMS_TEMPLATE,
  UPDATE_ORGANIZATION_LOGO,
  REMOVE_ORGANIZATION_LOGO
} from '@/store/actions.type';
import DefaultLayout from '@/layouts/DefaultLayout';

export default {
  name: 'account-settings',
  components: {
    DefaultLayout
  },
  data() {
    return {
      featureLimitedModal: false,
      featureLimitedText: '',
      featureLimitedDefaultText: 'To enable this feature, you need to upgrade your price plan.',
      settingsTabs: 0,
      eSignTemplates: [],
      eSignTableHeaders: [
        { text: 'Name', value: 'name', align: 'left' },
        { text: 'Ready', value: 'ready', align: 'left' }
      ],
      smsDialog: false,
      smsTemplateEdit: false,
      smsLoading: false,
      smsTemplates: [],
      smsTableHeaders: [
        { text: 'Type', value: 'typeString', align: 'left' },
        { text: 'Edit', value: 'edit', align: 'left' }
      ],
      smsSender: '',
      smsBody: '',
      smsType: 0,
      currentSmsTemplate: {
        sender: '',
        body: '',
        type: 0
      },
      currentTemplate: {},
      signersModel: [],
      setupESignTemplateModal: false,
      loading: false,
      userTypes: [
        {
          id: 1,
          title: 'App user'
        },
        {
          id: 2,
          title: 'Dashboard user'
        },
        {
          id: 3,
          title: 'Other'
        }
      ],
      signersError: false,
      signersErrorText: '',
      logo: [],
      createInProgress: false,
      removeInProgress: false,
      logoRequirements: [
        {
          label: 'File Format:',
          description: 'The logo must be in one of the following formats: PNG, JPEG, or WEBP.'
        },
        { label: 'File Size:', description: 'The maximum file size allowed is 130KB.' },
        {
          label: 'Dimensions:',
          description:
            'The logo should have a minimum resolution of 300x300 pixels. For best results, a resolution of 1000x1000 pixels is recommended.'
        },
        {
          label: 'Background:',
          description:
            'The logo should have a transparent background (for PNG and WEBP files) or a solid white background (for JPEG files).'
        }
      ]
    };
  },
  computed: {
    ...mapState({ organization: (state) => state.organizations.userSelectedOrganization }),
    ...mapState({
      limitations: (state) => state.organizations.userSelectedOrganization.limitations
    }),
    computedSmsTemplateTypes() {
      const types = [
        {
          type: 2,
          typeString: 'New profile request'
        },
        {
          type: 4,
          typeString: 'New profile request reminder'
        },
        {
          type: 5,
          typeString: 'Authenticator code'
        },
        {
          type: 6,
          typeString: 'New Anonymous Profile Request'
        }
      ];

      if (!this.smsTemplates.list) {
        return types;
      }

      this.smsTemplates.list.forEach((template) => {
        const typeIndex = types.findIndex((type) => type.type === template.type);
        if (typeIndex > -1) {
          types.splice(typeIndex, 1);
        }
      });

      return types;
    },
    smsSenderRules() {
      const required = (v) => !!v || 'Sender is required';
      const length = (v) => v.length < 12 || 'Max length 11 symbols';
      const symbolsRegExp = /^[\w]*$/;
      const symbols = (v) =>
        symbolsRegExp.test(String(v).toLowerCase()) || 'Please check the sender name';
      return [required, length, symbols];
    }
  },
  watch: {
    featureLimitedModal(val) {
      if (!val) {
        this.featureLimitedText = '';
      }
    }
  },
  methods: {
    async saveSettings() {
      this.loading = true;
      const data = {
        organizationId: this.organization.id,
        suppressCancelEmail: this.organization.suppressCancelRequestEmail
      };

      try {
        await this.$store.dispatch(SAVE_ACCOUNT_CUSTOM_SETTINGS, data);
        this.loading = false;
        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Settings saved'
        });
      } catch (err) {
        this.loading = false;
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Something went wrong - please contact support@planetverify.com'
        });
      }
    },
    validateSignersForm() {
      let formValid = true;

      this.signersModel.forEach((item) => {
        if (!item.userType) {
          formValid = false;
        }
      });

      if (!formValid) {
        this.signersError = true;
        this.signersErrorText = 'Please select user types for all roles';
      } else {
        this.signersError = false;
        this.signersErrorText = '';
      }

      return formValid;
    },
    async getSmsTemplates() {
      this.smsLoading = true;
      try {
        const response = await this.$store.dispatch(
          GET_ACCOUNT_SMS_TEMPLATES,
          this.organization.id
        );
        if (!response.list) {
          response.list = [];
        }
        this.smsTemplates = response;
        this.smsLoading = false;
        this.smsTemplates.list.forEach((el) => {
          switch (el.type) {
            case 1:
              el.typeString = 'AppUserInvite';
              break;
            case 2:
              el.typeString = 'NewProfileRequest';
              break;
            case 3:
              el.typeString = 'AppUserInviteReminder';
              break;
            case 4:
              el.typeString = 'NewProfileRequestReminder';
              break;
            case 5:
              el.typeString = 'AuthenticatorCode';
              break;
            case 6:
              el.typeString = 'NewAnonymousProfileRequest';
              break;
          }
        });
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Error!',
          text: 'Error getting SMS templates. Please contact support@planetverify.com'
        });
        this.smsLoading = false;
      }
    },
    addAccountSmsTemplate() {
      this.smsTemplateEdit = false;
      this.currentSmsTemplate.id = uuidv4();
      this.currentSmsTemplate.accountId = this.organization.id;
      this.currentSmsTemplate.body = '';
      this.currentSmsTemplate.sender = '';
      this.currentSmsTemplate.type = 0;
      this.smsDialog = true;
    },
    editSmsTemplate(template) {
      this.smsTemplateEdit = true;
      this.currentSmsTemplate.id = template.id;
      this.currentSmsTemplate.accountId = template.accountId;
      this.currentSmsTemplate.body = template.body;
      this.currentSmsTemplate.sender = template.sender;
      this.currentSmsTemplate.type = template.type;
      this.smsDialog = true;
    },
    async saveSmsTemplate() {
      if (this.$refs.smsTemplateForm.validate()) {
        try {
          await this.$store.dispatch(UPDATE_CUSTOM_SMS_TEMPLATE, this.currentSmsTemplate);
          this.$notify({
            type: 'success',
            title: 'Success',
            text: 'SMS Template Updated'
          });
          this.getSmsTemplates();
          this.smsDialog = false;
        } catch (err) {
          this.$notify({
            type: 'error',
            title: 'Error!',
            text: 'Error updating SMS template. Please contact support@planetverify.com'
          });
          this.smsDialog = false;
        }
      }
    },
    async addLogo() {
      this.createInProgress = true;

      const formData = new FormData();
      this.logo.forEach((f) => {
        formData.append('files', f);
      });
      formData.append('OrganizationId', this.organization.id);

      try {
        await this.$store.dispatch(UPDATE_ORGANIZATION_LOGO, formData);
      } catch (err) {
        this.createInProgress = false;
      } finally {
        this.logo = [];
        await this.$store.dispatch(CHECK_ORGANIZATIONS);
        this.createInProgress = false;
      }
    },
    async removeLogo() {
      this.removeInProgress = true;
      try {
        await this.$store.dispatch(REMOVE_ORGANIZATION_LOGO, {
          OrganizationId: this.organization.id
        });
      } catch (err) {
        this.removeInProgress = false;
      } finally {
        await this.$store.dispatch(CHECK_ORGANIZATIONS);
        this.removeInProgress = false;
      }
    }
  },
  async mounted() {
    await this.$store.dispatch(CHECK_ORGANIZATIONS);
    if (this.$store.getters.isSystemAdmin) {
      this.getSmsTemplates();
    }
  }
};
</script>

<style>
.text-left {
  text-align: left !important;
}
.templates-table td,
.templates-table th {
  padding: 5px;
}
.templates-table thead {
  border-bottom: 1px solid #ccc;
}
.textarea-row .v-input {
  height: auto !important;
}
.textarea-row .v-input__slot {
  height: auto !important;
}
.button-color {
  color: #fff;
}
</style>
